import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Check, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader, Stat } from "@/components/brand";
import { Button } from "@/components/ui/button";

const gbp = (minor) => `£${(minor / 100).toFixed(2)}`;

export default function Billing() {
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = () => {
    api.get("/billing/summary").then((r) => setS(r.data));
    api.get("/billing/invoices").then((r) => setInvoices(r.data));
  };
  useEffect(() => {
    load();
    api.get("/public/plans").then((r) => setPlans(r.data));
    api.get("/public/credit-packs").then((r) => setPacks(r.data));
    const ref = params.get("ref");
    if (ref) api.get(`/billing/orders/${ref}/status`).then((r) => { toast[r.data.status === "paid" ? "success" : "message"](r.data.status === "paid" ? "Payment confirmed" : `Payment status: ${r.data.status}`); load(); }).catch((e) => toast.error(errMsg(e)));
  }, [params]);
  const checkout = async (kind, slug) => {
    setBusy(slug);
    try {
      const { data } = await api.post("/billing/checkout", { kind, slug, interval: annual ? "year" : "month" });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };
  if (!s) return <Loader />;
  const sub = s.subscription || {};
  return (
    <div className="mx-auto max-w-7xl space-y-8" data-testid="billing-page">
      <div><p className="eyebrow">Billing & credits</p><h1 className="mt-2 text-3xl font-bold">Your subscription</h1></div>
      {!s.payments_configured && <div className="rounded-xl border border-[#FFA000]/30 bg-[#FFA000]/10 p-4 text-sm" data-testid="payments-not-configured">Online payments are not yet enabled by Saflr. Plan upgrades and top-ups will be available once payment processing is configured.</div>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Plan" value={sub.plan_name || "—"} hint={`${(sub.status || "").replace("_", " ")} · ${sub.interval || ""}`} testid="billing-plan" />
        <Stat label="Period ends" value={sub.period_end?.slice(0, 10) || "—"} tone={["past_due", "trial_expired"].includes(sub.status) ? "red" : "navy"} />
        <Stat label="Credits available" value={s.balance} tone="green" icon={Coins} testid="billing-balance" />
        <Stat label="Monthly / purchased" value={`${s.credit_monthly} / ${s.credit_purchased}`} hint="Monthly credits reset each period" />
      </div>
      <section>
        <div className="mb-4 flex items-center justify-between"><h2 className="text-xl font-semibold">Change plan</h2>
          <div className="inline-flex rounded-full border p-1 text-sm">{[["Monthly", false], ["Annual", true]].map(([l, v]) => <button key={l} onClick={() => setAnnual(v)} className={`rounded-full px-4 py-1 ${annual === v ? "bg-primary text-primary-foreground" : ""}`}>{l}</button>)}</div></div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((p) => (
            <div key={p.slug} className={`flex flex-col rounded-2xl border bg-card p-6 ${sub.plan_slug === p.slug ? "ring-2 ring-health" : ""}`} data-testid={`billing-plan-${p.slug}`}>
              <h3 className="font-semibold">{p.name}</h3>
              <p className="mt-2 font-display text-3xl font-semibold">{p.monthly_price == null ? "Custom" : `£${annual ? p.annual_price : p.monthly_price}`}<span className="text-sm font-normal text-muted-foreground">{p.monthly_price != null && (annual ? "/yr" : "/mo")}</span></p>
              <p className="text-xs text-muted-foreground">+ VAT · {p.monthly_credits} credits/mo · {p.user_limit} users</p>
              <ul className="mt-4 flex-1 space-y-1.5 text-sm">{(p.highlights || []).slice(0, 4).map((h) => <li key={h} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-health" />{h}</li>)}</ul>
              {p.contact_sales ? <Button asChild variant="outline" className="mt-4"><a href="/contact">Contact sales</a></Button> : (
                <Button className="mt-4" variant={sub.plan_slug === p.slug && sub.status === "active" ? "outline" : "default"} disabled={!s.payments_configured || busy} onClick={() => checkout("plan", p.slug)} data-testid={`checkout-plan-${p.slug}`}>
                  {busy === p.slug && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{sub.plan_slug === p.slug && sub.status === "active" ? "Renew" : "Choose"}
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>
      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-6"><h2 className="font-semibold">AI credit top-ups</h2>
          <div className="mt-4 space-y-2">{packs.map((p) => <div key={p.slug} className="flex items-center justify-between rounded-xl border p-3"><span className="text-sm">{p.name} · £{p.price} + VAT</span><Button size="sm" variant="outline" disabled={!s.payments_configured || busy} onClick={() => checkout("credits", p.slug)} data-testid={`checkout-pack-${p.slug}`}>Buy</Button></div>)}</div>
        </div>
        <div className="rounded-2xl border bg-card p-6"><h2 className="font-semibold">Invoices</h2>
          {invoices.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No invoices yet.</p> : <ul className="mt-3 divide-y text-sm">{invoices.map((i) => <li key={i.id} className="flex justify-between py-2"><span>{i.number} · {i.description}</span><span className="font-mono">{gbp(i.gross_minor)} <span className="text-xs text-muted-foreground">(VAT {i.vat_rate}%)</span></span></li>)}</ul>}
        </div>
      </section>
      <section className="rounded-2xl border bg-card p-6"><h2 className="font-semibold">Credit history</h2>
        <ul className="mt-3 divide-y text-sm" data-testid="credit-ledger">{s.ledger.map((l) => <li key={l.id} className="flex justify-between gap-4 py-2"><span className="truncate">{l.reason}</span><span className="flex shrink-0 gap-4"><span className="text-xs text-muted-foreground">{l.created_at.slice(0, 16).replace("T", " ")}</span><span className={`font-mono ${l.delta < 0 ? "text-alert" : "text-health"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span></span></li>)}</ul>
      </section>
    </div>
  );
}
